import classNames from "classnames"

import TextButton from "./Buttons/TextButton"
import GatsbyStoryblokImage from "./GatsbyStoryblokImage"
import ArrowIcon from "./Icons/ArrowIcon"
import StarIcon from "./Icons/StarIcon"
import Typography, { TypographySize } from "./Typography"
import { Color } from "../../../constants/V2/color"

import useTrustedByText, {
  TRUSTED_BY_BUSINESSES_TOKEN,
} from "@hooks/V2/useTrustedByText"
import { getBorderColorClass } from "@utils/V2/color"
import interpolateTemplate from "@utils/V2/interpolateTemplate"
import React from "react"

export type CalloutProps = {
  color?: Color
  classname?: string
  text: string
  textSize?: TypographySize
  highlightedWord: string
  highlightColor?: Color
  highlightSize?: TypographySize
  hasBorder?: boolean
  hasIcon?: boolean
  ctaText?: string
  ctaLink?: string | YocoCom.YocoLink
  iconUrl?: string
  iconAlt?: string
  iconColor?: Color
  iconClassName?: string
  animate?: boolean
}

const Callout = ({
  color = Color.Charcoal,
  classname,
  text,
  textSize = "body-md",
  highlightedWord,
  highlightColor = Color.Blue,
  highlightSize = "body-lg",
  hasBorder = true,
  hasIcon = true,
  ctaText,
  ctaLink,
  iconUrl,
  iconAlt,
  iconColor = color,
  iconClassName,
  animate = false,
}: CalloutProps) => {
  const textUsesTrustedByToken = text.includes(TRUSTED_BY_BUSINESSES_TOKEN)
  const highlightedWordUsesTrustedByToken = highlightedWord.includes(
    TRUSTED_BY_BUSINESSES_TOKEN
  )

  const {
    text: resolvedText,
    highlightedText: resolvedTrustedCount,
    intersectionElementRef,
  } = useTrustedByText({
    template: text,
    valueKey: "trustedByNumBusinesses",
    isAnimated: textUsesTrustedByToken && animate,
  })

  const finalText = textUsesTrustedByToken ? resolvedText : text
  const finalHighlightedWord = highlightedWordUsesTrustedByToken
    ? interpolateTemplate(highlightedWord, {
        trustedByNumBusinesses: resolvedTrustedCount,
      })
    : highlightedWord

  return (
    <div
      ref={
        textUsesTrustedByToken && animate ? intersectionElementRef : undefined
      }
      className={classNames(
        getBorderColorClass(color),
        "flex w-fit flex-row items-center justify-center gap-x-4",
        {
          "rounded-40 border border-black border-opacity-10 px-8 pb-[9.05px] pt-[8.9px] md:px-16 md:py-[8px]":
            hasBorder,
        },
        classname
      )}
    >
      {hasIcon ? (
        iconUrl ? (
          <GatsbyStoryblokImage
            aspectRatio="fullWidth"
            loadType="eager"
            image={iconUrl}
            alt={`Icon for ${iconAlt}`}
            width={16}
          />
        ) : (
          <StarIcon
            color={iconColor}
            className={classNames(
              "md:-mt-[1px]",
              { "-mt-[3px]": !hasBorder },
              iconClassName
            )}
          />
        )
      ) : null}

      <div
        className={classNames("flex gap-x-4", {
          "mt-[2px] md:-mb-[1px]": hasBorder,
        })}
      >
        <Typography
          color={color}
          text={finalText}
          font="grotesk"
          size={textSize}
          weight="book"
          highlightColor={highlightColor}
          highlightedText={finalHighlightedWord}
          highlightSize={highlightSize}
          highlightWeight="medium"
        />
        {ctaText && ctaLink && (
          <TextButton
            text={ctaText}
            linkUrl={ctaLink}
            color={highlightColor}
            style="icon"
            icon={<ArrowIcon />}
            className="ml-10 pt-[2px]"
          />
        )}
      </div>
    </div>
  )
}

export default Callout
